/**
 * lightbox — turn any element into the trigger for the image viewer.
 *
 * The directive is a TRIGGER and nothing else: it calls a plain `show()` with
 * `<schmancy-lightbox>` as the content. It builds no overlay of its own, so
 * the viewer inherits the focus trap, Escape, dismissal, and history handling.
 * Its trigger supplies the source rectangle, so the image expands over that
 * rectangle from its top-left corner regardless of where it was activated.
 *
 * The trigger is made operable, not just clickable. An `<img>` with a click
 * handler is unreachable by keyboard, so unless the consumer has already
 * said otherwise the element gains `role="button"`, `tabindex="0"`, and
 * Enter/Space activation. Everything the directive adds — listeners,
 * attributes, the cursor affordance — is recorded and undone on teardown,
 * so a directive that disconnects leaves the element as it found it.
 *
 * Options are read at ACTIVATION, never captured when the listener is
 * bound, so a re-render that changes `images` or `index` takes effect on
 * the next open rather than replaying whatever the first render passed.
 */
import { componentText } from '@schmancy/mixins'
import { show } from '@schmancy/overlay/overlay.service'
import type { ResolvedShowOptions } from '@schmancy/overlay/overlay.types'
import { noChange } from 'lit'
import { AsyncDirective } from 'lit/async-directive.js'
import { directive, ElementPart, PartInfo, PartType } from 'lit/directive.js'
import { Subscription } from 'rxjs'
import './lightbox'

export interface LightboxOptions {
	images?: string[]
	index?: number
	/** Accessible name for the viewer AND for the trigger the directive
	 *  re-roles into a button; falls back to the trigger's own alt text. */
	label?: string
	/** Per-image accessible names, indexed with `images`. */
	labels?: string[]
}

class LightboxDirective extends AsyncDirective {
	private element?: HTMLElement
	private options?: LightboxOptions
	private open$?: Subscription

	private clickHandler?: (e: Event) => void
	private keydownHandler?: (e: KeyboardEvent) => void

	/** Attributes this directive added, so teardown removes exactly those and
	 *  leaves any the consumer set themselves alone. */
	private addedAttributes: string[] = []
	private addedCursor = false

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) {
			throw new Error('lightbox directive can only be used on elements')
		}
	}

	render(options?: LightboxOptions) {
		void options
		return noChange
	}

	update(part: ElementPart, [options]: [LightboxOptions?]) {
		// Stored on the instance rather than closed over by the listener: the
		// listener is bound once, but every activation must see the options
		// from the LATEST render.
		this.options = options
		this.element = part.element as HTMLElement

		if (!this.clickHandler) this.attach()

		return noChange
	}

	private attach() {
		const el = this.element
		if (!el) return

		this.clickHandler = () => this.open()
		el.addEventListener('click', this.clickHandler)

		// Keyboard operability. An <img role="button"> is announced and
		// reachable; without this the viewer is mouse-only.
		this.keydownHandler = (e: KeyboardEvent) => {
			if (e.key !== 'Enter' && e.key !== ' ') return
			e.preventDefault()
			this.open()
		}
		el.addEventListener('keydown', this.keydownHandler)

		// Only ever ADD what is absent — a consumer who already gave the
		// element a role, a tabindex, or a label knows their context better.
		this.addIfAbsent('role', 'button')
		this.addIfAbsent('tabindex', '0')

		// A button is named from its contents, and a trigger that carries none
		// — an empty <div>, a background-image tile, an <img> — ships as an
		// unnamed button (measured: axe `aria-command-name`). The name the
		// consumer already gave the viewer, or the picture's own alt text, is
		// the name the button they just got should carry.
		const name = this.options?.label || (el instanceof HTMLImageElement ? el.alt : '')
		if (name) this.addIfAbsent('aria-label', name)

		if (!el.style.cursor) {
			el.style.cursor = 'pointer'
			this.addedCursor = true
		}
	}

	private addIfAbsent(name: string, value: string) {
		const el = this.element
		if (!el || el.hasAttribute(name)) return
		el.setAttribute(name, value)
		this.addedAttributes.push(name)
	}

	private open() {
		const el = this.element
		if (!el) return

		const configured = this.options?.images ?? []
		const fallback = el instanceof HTMLImageElement ? el.currentSrc || el.src : ''
		const images = configured.length > 0 ? configured : fallback ? [fallback] : []
		if (images.length === 0) return

		const label = this.options?.label || (el instanceof HTMLImageElement ? el.alt : '') || ''
		const gallery = images.length > 1

		// One viewer at a time per trigger.
		this.open$?.unsubscribe()
		// The internal shape, like `schmancy-boat`'s: `layout` is the one thing a
		// caller may name and it is reachable only from inside this package, so a
		// consumer can never put a viewer's full-viewport ground under a menu. The
		// source element supplies its box independently of where it was activated.
		const opts: ResolvedShowOptions = {
			// The overlay owns source-bound placement and the frameless material.
			layout: 'viewer',
			trigger: el,
			ariaLabel: label || this.options?.labels?.[this.options?.index ?? 0] || componentText('lightbox.viewer'),
			props: {
				images: gallery ? images : [],
				src: gallery ? '' : images[0],
				initialIndex: this.options?.index ?? 0,
				label,
				labels: this.options?.labels ?? [],
			},
		}
		this.open$ = show('schmancy-lightbox', opts).subscribe()
	}

	private detach() {
		const el = this.element
		if (el) {
			if (this.clickHandler) el.removeEventListener('click', this.clickHandler)
			if (this.keydownHandler) el.removeEventListener('keydown', this.keydownHandler)
			for (const name of this.addedAttributes) el.removeAttribute(name)
			if (this.addedCursor) el.style.removeProperty('cursor')
		}
		this.clickHandler = undefined
		this.keydownHandler = undefined
		this.addedAttributes = []
		this.addedCursor = false
	}

	disconnected() {
		this.detach()
		// A trigger that leaves the tree must not strand its own open viewer.
		this.open$?.unsubscribe()
		this.open$ = undefined
	}

	reconnected() {
		// AsyncDirective can be re-attached after disconnect; without this the
		// trigger would come back inert, having had its listeners removed.
		if (!this.clickHandler) this.attach()
	}
}

export const lightbox = directive(LightboxDirective)
